import './WidgetPalette.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { getFeatures } from 'src/module/authentication/selector';
import { getWidgetRegistry, WidgetRegistryEntry } from './widgetRegistry';
import { useTranslation } from 'src/i18n';

interface WidgetPaletteProps {
  open: boolean;
  onClose: () => void;
  onAdd: (entry: WidgetRegistryEntry) => void;
  currentRows: any[];
}

function isWidgetPresent (rows: any[], entry: WidgetRegistryEntry): boolean {
  return rows.some((row) => {
    const componentName = row?.children?.[0]?.componentName || row?.children?.[0]?.children?.[0]?.componentName;
    const blockTitle = row?.children?.[0]?.title;
    const entryComponentName = entry.defaultLayout?.['children']?.[0]?.componentName || entry.defaultLayout?.['children']?.[0]?.children?.[0]?.componentName;
    const entryBlockTitle = entry.defaultLayout?.['children']?.[0]?.title;

    if (entryComponentName && componentName === entryComponentName) return true;
    if (entryBlockTitle && blockTitle === entryBlockTitle) return true;
    return false;
  });
}

export default function WidgetPalette ({ open, onClose, onAdd, currentRows }: WidgetPaletteProps) {
  const { t } = useTranslation();
  const features = useSelector(getFeatures);

  const isFeatureAccessible = (feature: string | null): boolean => {
    if (!feature) return true;
    return features.includes(feature);
  };

  const registry = getWidgetRegistry(t);
  const visibleWidgets = registry.filter((entry) => isFeatureAccessible(entry.feature));

  return (
    <Drawer anchor='right' open={open} onClose={onClose}>
      <div className='widget-palette'>
        <div className='widget-palette-header'>
          <h3>{t('dashboard.addCard')}</h3>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </div>
        <List>
          {visibleWidgets.map((entry) => {
            const alreadyAdded = entry.singleton && isWidgetPresent(currentRows, entry);
            return (
              <ListItem key={entry.key} className='widget-palette-item'>
                <ListItemText
                  primary={t(entry.label as any)}
                  secondary={t(entry.description as any)}
                />
                <Button
                  variant='outlined'
                  disabled={alreadyAdded}
                  onClick={() => onAdd(entry)}
                >
                  {alreadyAdded ? t('dashboard.alreadyAdded') : t('dashboard.addCard')}
                </Button>
              </ListItem>
            );
          })}
        </List>
      </div>
    </Drawer>
  );
}
